import React, { useState, useEffect } from 'react';
import { SubscriptionPlan } from '../../../services/subscriptionPlanService';
import FeaturesList from './FeaturesList';
import { Loader } from 'lucide-react';

interface PlanFormProps {
  plan?: SubscriptionPlan | null;
  onSubmit: (formData: any) => Promise<void>;
  isSubmitting: boolean;
}

interface FormData {
  name: string;
  description: string;
  price: string;
  interval: 'monthly' | 'yearly';
  features: string[];
  isActive: boolean;
}

interface FormErrors {
  name?: string;
  description?: string;
  price?: string;
  interval?: string;
  features?: string[];
}

const PlanForm: React.FC<PlanFormProps> = ({ plan, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    price: '',
    interval: 'monthly',
    features: [],
    isActive: true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Pre-populate form for edit mode
  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        description: plan.description || '',
        price: plan.price.toString(),
        interval: plan.interval,
        features: plan.features || [],
        isActive: plan.isActive,
      });
    }
  }, [plan]);

  // Validation logic
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Plan name must not exceed 100 characters';
    }

    // Description validation
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    // Price validation
    if (!formData.price) {
      newErrors.price = 'Price is required';
    } else {
      const priceNum = parseFloat(formData.price);
      if (isNaN(priceNum) || priceNum <= 0) {
        newErrors.price = 'Price must be greater than 0';
      } else if (!/^\d+(\.\d{1,2})?$/.test(formData.price)) {
        newErrors.price = 'Price must have max 2 decimal places';
      } else if (priceNum > 9999.99) {
        newErrors.price = 'Price must not exceed $9,999.99';
      }
    }

    // Interval validation
    if (!['monthly', 'yearly'].includes(formData.interval)) {
      newErrors.interval = 'Interval must be monthly or yearly';
    }

    // Features validation
    const featureErrors: string[] = [];
    if (formData.features.length > 20) {
      featureErrors.push('Maximum 20 features allowed');
    }
    formData.features.forEach((feature, index) => {
      if (feature.length > 100) {
        featureErrors.push(`Feature ${index + 1} must not exceed 100 characters`);
      }
    });
    if (featureErrors.length > 0) {
      newErrors.features = featureErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation on blur
  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateForm();
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // For edit mode, only send editable fields
    if (plan) {
      const submitData = {
        description: formData.description.trim() || undefined,
        features: formData.features.filter(f => f.trim()),
        isActive: formData.isActive,
      };
      await onSubmit(submitData);
    } else {
      // For create mode, send all fields
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price: parseFloat(formData.price),
        interval: formData.interval,
        features: formData.features.filter(f => f.trim()),
        isActive: formData.isActive,
      };
      await onSubmit(submitData);
    }
  };

  const isFormValid = Object.keys(errors).length === 0 && formData.name.trim() && formData.price;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Plan Name */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Plan Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={e => handleChange('name', e.target.value)}
          onBlur={() => handleBlur('name')}
          placeholder="e.g., Pro Monthly"
          maxLength={100}
          disabled={plan !== null && plan !== undefined} // Disable for edit mode
          className="w-full bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#23F47C] transition-colors placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {errors.name && touched.name && (
          <p className="mt-1 text-sm text-red-400">{errors.name}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={e => handleChange('description', e.target.value)}
          onBlur={() => handleBlur('description')}
          placeholder="Describe what's included in this plan"
          maxLength={500}
          rows={3}
          className="w-full bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#23F47C] transition-colors placeholder-gray-500 resize-none"
        />
        {errors.description && touched.description && (
          <p className="mt-1 text-sm text-red-400">{errors.description}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">{formData.description.length}/500</p>
      </div>

      {/* Price and Interval */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Price (USD) <span className="text-red-400">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.price}
            onChange={e => handleChange('price', e.target.value)}
            onBlur={() => handleBlur('price')}
            placeholder="19.99"
            disabled={plan !== null && plan !== undefined} // Disable for edit mode
            className="w-full bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#23F47C] transition-colors placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {errors.price && touched.price && (
            <p className="mt-1 text-sm text-red-400">{errors.price}</p>
          )}
        </div>

        {/* Interval */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Billing Interval <span className="text-red-400">*</span>
          </label>
          <select
            value={formData.interval}
            onChange={e => handleChange('interval', e.target.value)}
            onBlur={() => handleBlur('interval')}
            disabled={plan !== null && plan !== undefined} // Disable for edit mode
            className="w-full bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#23F47C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
          {errors.interval && touched.interval && (
            <p className="mt-1 text-sm text-red-400">{errors.interval}</p>
          )}
        </div>
      </div>

      {/* Features */}
      <div>
        <FeaturesList
          features={formData.features}
          onChange={features => handleChange('features', features)}
          errors={errors.features}
        />
      </div>

      {/* Active Status */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.isActive}
          onChange={e => handleChange('isActive', e.target.checked)}
          className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-[#23F47C] focus:ring-[#23F47C]"
        />
        <label htmlFor="isActive" className="text-sm font-medium text-gray-300">
          Active (available for purchase)
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-700">
        <button
          type="submit"
          disabled={!isFormValid || isSubmitting}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#23F47C] text-gray-900 px-6 py-2 rounded-lg font-medium hover:bg-[#1dd45f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting && <Loader size={18} className="animate-spin" />}
          {plan ? 'Update Plan' : 'Create Plan'}
        </button>
      </div>
    </form>
  );
};

export default PlanForm;
